import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { Calendar, Clock, MapPin, DollarSign } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface Nutritionist {
  id: string;
  name: string;
}

interface Pricing {
  id: string;
  consultation_type: string;
  consultation_format: string;
  price: number;
  duration_minutes: number;
}

export default function NutritionistBookingPage() {
  const { id } = useParams<{ id: string }>();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [nutritionist, setNutritionist] = useState<Nutritionist | null>(null);
  const [pricingOptions, setPricingOptions] = useState<Pricing[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [consultationType, setConsultationType] = useState('');
  const [consultationFormat, setConsultationFormat] = useState('');
  const [location, setLocation] = useState('');
  const [specialNotes, setSpecialNotes] = useState('');

  const loadNutritionist = useCallback(async () => {
    if (!id) return;

    try {
      const { data: nutritionistData, error: nutritionistError } = await supabase
        .from('nutritionists')
        .select('id, name')
        .eq('id', id)
        .single();

      if (nutritionistError) throw nutritionistError;
      setNutritionist(nutritionistData as Nutritionist);

      const { data: pricingData, error: pricingError } = await supabase
        .from('nutritionist_pricing')
        .select('*')
        .eq('nutritionist_id', id);

      if (!pricingError && pricingData) {
        setPricingOptions(pricingData as Pricing[]);
        if (pricingData.length > 0) {
          setConsultationType(pricingData[0].consultation_type);
          setConsultationFormat(pricingData[0].consultation_format);
        }
      }
    } catch (error) {
      console.error('Error loading nutritionist:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadNutritionist();
    }
  }, [id, loadNutritionist]);

  useEffect(() => {
    if (!profile || profile.role !== 'client') {
      navigate('/login');
    }
  }, [profile, navigate]);

  const selectedPricing = pricingOptions.find(
    (p) => p.consultation_type === consultationType && p.consultation_format === consultationFormat
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile || !nutritionist || !selectedDate || !selectedTime || !selectedPricing) return;

    setSubmitting(true);

    try {
      const startDateTime = new Date(`${selectedDate}T${selectedTime}`);

      const { error } = await supabase.from('home_bookings').insert({
        client_id: profile.id,
        booking_type: 'nutritionist',
        nutritionist_id: nutritionist.id,
        consultation_type: consultationType,
        consultation_format: consultationFormat,
        start_time: startDateTime.toISOString(),
        duration_minutes: selectedPricing.duration_minutes,
        address: location || null,
        total_amount: selectedPricing.price,
        status: 'pending',
        special_notes: specialNotes || null,
      });

      if (error) throw error;

      alert('Booking created successfully!');
      navigate('/bookings');
    } catch (error) {
      console.error('Error creating booking:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create booking. Please try again.';
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const minDate = new Date().toISOString().split('T')[0];

  if (loading) {
    return (
      <>
        <Header />
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
            <p className="mt-4 text-sm text-gray-600">Loading...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!nutritionist) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-gray-50 py-8">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="rounded-xl border border-red-200 bg-red-50 p-8 text-center">
              <h2 className="text-xl font-semibold text-red-900">Nutritionist Not Found</h2>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="min-h-screen bg-gray-50 py-8">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">
              Book Consultation with {nutritionist.name}
            </h1>
            <p className="mt-2 text-gray-600">
              Fill in the details to book your nutrition consultation
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Consultation Type */}
            {pricingOptions.length > 0 && (
              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-700">
                  Consultation Type
                </label>
                <select
                  value={consultationType}
                  onChange={(e) => setConsultationType(e.target.value)}
                  required
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                >
                  <option value="">Select type</option>
                  {Array.from(new Set(pricingOptions.map((p) => p.consultation_type))).map(
                    (type) => (
                      <option key={type} value={type}>
                        {type.replace(/([A-Z])/g, ' $1').trim()}
                      </option>
                    )
                  )}
                </select>
              </div>
            )}

            {/* Consultation Format */}
            {consultationType && (
              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-700">
                  Consultation Format
                </label>
                <select
                  value={consultationFormat}
                  onChange={(e) => setConsultationFormat(e.target.value)}
                  required
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                >
                  <option value="">Select format</option>
                  {pricingOptions
                    .filter((p) => p.consultation_type === consultationType)
                    .map((p) => (
                      <option key={p.id} value={p.consultation_format}>
                        {p.consultation_format.replace(/([A-Z])/g, ' $1').trim()}
                      </option>
                    ))}
                </select>
              </div>
            )}

            {/* Date Selection */}
            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-700">
                <Calendar className="mr-2 inline h-4 w-4" />
                Select Date
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                min={minDate}
                required
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>

            {/* Time Selection */}
            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-700">
                <Clock className="mr-2 inline h-4 w-4" />
                Select Time
              </label>
              <input
                type="time"
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
                required
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>

            {/* Location (for in-person) */}
            {consultationFormat === 'inPerson' && (
              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-700">
                  <MapPin className="mr-2 inline h-4 w-4" />
                  Location Address
                </label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Enter address for in-person consultation"
                  required
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
            )}

            {/* Special Notes */}
            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-700">
                Special Notes (Optional)
              </label>
              <textarea
                value={specialNotes}
                onChange={(e) => setSpecialNotes(e.target.value)}
                rows={4}
                placeholder="Any special requests or dietary concerns..."
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>

            {/* Summary */}
            {selectedPricing && (
              <div className="rounded-xl border border-gray-200 bg-white p-6">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">Booking Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Nutritionist:</span>
                    <span className="font-semibold text-gray-900">{nutritionist.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Type:</span>
                    <span className="font-semibold text-gray-900">
                      {consultationType.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Format:</span>
                    <span className="font-semibold text-gray-900">
                      {consultationFormat.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Duration:</span>
                    <span className="font-semibold text-gray-900">
                      {selectedPricing.duration_minutes} minutes
                    </span>
                  </div>
                  <div className="border-t border-gray-200 pt-2">
                    <div className="flex justify-between">
                      <span className="text-lg font-semibold text-gray-900">Total:</span>
                      <span className="text-lg font-bold text-green-600">
                        <DollarSign className="inline h-5 w-5" />
                        {selectedPricing.price.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-3 font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !selectedPricing}
                className="flex-1 rounded-lg bg-green-600 px-4 py-3 font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting ? 'Booking...' : 'Confirm Booking'}
              </button>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </>
  );
}
